import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  alpha,
  Dialog,
  Stack,
  Theme,
  ThemeProvider,
  Typography,
} from '@rossum/ui/material';
import { HTML_FONT_SIZE, resolveTheme } from '@rossum/ui/theme';
import { updateOrganization } from '../../redux/modules/organization/actions';
import { safeOrganizationSelector } from '../../redux/modules/organization/selectors';
import { userSelector } from '../../redux/modules/user/selectors';
import { onboardingData } from './surveyData';
import TrialOnboardingSurvey from './TrialOnboardingSurvey';
import { WelcomeScreen } from './WelcomeScreen';

// shared across registration app and Elis as background during onboarding
const BG_1920 =
  'https://rossum-public-resources.app.rossum.ai/images/app_blur_1920.png';
const BG_1440 =
  'https://rossum-public-resources.app.rossum.ai/images/app_blur_1440.png';

const backdropStyle = {
  backgroundColor: (theme: Theme) =>
    alpha(
      theme.palette.mode !== 'dark'
        ? theme.palette.common.black
        : theme.palette.common.white,
      0.85
    ),
  backgroundImage: `url(${BG_1440})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  '@media only screen and (min-width: 1440px)': {
    backgroundImage: `url(${BG_1920})`,
  },
};

const TrialOnboarding = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const user = useSelector(userSelector);

  const isSurveyComplete =
    user.uiSettings.onboardingSurvey?.activeStep === 'what-is-your-role';

  const stepData = isSurveyComplete
    ? onboardingData.onboardingSteps.complete
    : onboardingData.surveySteps['what-is-your-role'];

  const organization = useSelector(safeOrganizationSelector);

  useEffect(() => {
    const sourceValue = user.username.includes('@rossum.ai')
      ? 'internal'
      : 'registration';

    if (organization && organization.metadata.source !== sourceValue) {
      dispatch(
        updateOrganization({
          ...organization,
          metadata: {
            ...organization.metadata,
            source: sourceValue,
          },
        })
      );
    }
  }, [dispatch, organization, user.username]);

  return (
    <ThemeProvider
      theme={() =>
        resolveTheme('dark', {
          typography: {
            htmlFontSize: HTML_FONT_SIZE,
          },
        })
      }
    >
      <Dialog
        BackdropProps={{
          sx: backdropStyle,
        }}
        PaperProps={{
          sx: {
            minHeight: '100%',
            width: '100%',
            background: 'transparent',
            pt: { xs: 8, xl: 15 },
            pb: 8,
            px: 2,
            m: 0,
          },
        }}
        open
        maxWidth={false}
        data-cy="add-first-queue-modal"
      >
        <Stack
          alignItems="center"
          spacing={{ xs: 2, xl: 4 }}
          sx={{ width: '100%' }}
        >
          <Stack sx={{ alignItems: 'center', textAlign: 'center' }}>
            <Typography variant="h3">
              {intl.formatMessage({ id: stepData.titleId })}
            </Typography>
            <Typography variant="body1">
              {intl.formatMessage({ id: stepData.subtitleId })}
            </Typography>
            {'noteId' in stepData && (
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: stepData.noteId })}
              </Typography>
            )}
          </Stack>

          {isSurveyComplete ? <WelcomeScreen /> : <TrialOnboardingSurvey />}
        </Stack>
      </Dialog>
    </ThemeProvider>
  );
};

export default TrialOnboarding;
